import { Star } from "lucide-react";

import Text from "@/blueprint/components/Text/Text";
import ShopPayInstallments from "@/components/ShopPayInstallments/ShopPayInstallments";
import type { ShopPayInstallmentsTerms } from "@/lib/shopPayInstallments";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";
import { CONFIGURATOR } from "@/views/shop/shopContent";
import { useTrack } from "@/analytics/events";

type ConfiguratorTitleProps = {
  /** Defaults to the shop's own heading. */
  title?: string;
  /** Shopify's installments line under the rating; null leaves it out. */
  installments?: ShopPayInstallmentsTerms | null;
  className?: string;
};

const ConfiguratorTitle = ({
  title = CONFIGURATOR.title,
  installments = null,
  className,
}: ConfiguratorTitleProps) => {
  const reducedMotion = usePrefersReducedMotion();
  const track = useTrack();

  const handleReviewsClick = () => {
    track("cta_clicked", { cta: "reviews", href: "#reviews" });
    document.getElementById("reviews")?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-[16px] sd:max-w-[382px] sd:gap-xs",
        className,
      )}
    >
      <Text variant="configurator-title" as="h2">
        {title}
      </Text>
      {/* Figma 5363:11940 ("Configure your Matic" > Title > Container): the row
        is the rating text's own 18px line box, and the 16px star anchors 1px
        from its top rather than centering. Top-anchoring matters above
        1600px, where the line box grows to 20px but the star stays at 1px. */}
      <div className="flex items-start gap-xs text-brand-black">
        <Star
          aria-hidden="true"
          className="mt-px h-[16px] w-[16px] shrink-0 fill-current"
          strokeWidth={1.5}
        />
        <Text variant="paragraph-sm" as="p">
          {CONFIGURATOR.reviewAverage} from{" "}
          <button
            type="button"
            aria-label={`See ${CONFIGURATOR.reviewCount} reviews`}
            aria-controls="reviews"
            onClick={handleReviewsClick}
            className="underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-content-primary"
          >
            {CONFIGURATOR.reviewCount} reviews
          </button>
        </Text>
      </div>
      {installments !== null && <ShopPayInstallments terms={installments} />}
    </div>
  );
};

export default ConfiguratorTitle;
